// client/src/realtime/cachePatchHelpers.ts
// Pure functions for patching RTK Query cache in response to realtime events
// These can be unit tested independently of the realtime system

import type { ThreadPayload, MessagePayload, MessageReactionPayload, ReactionSummary } from './events';
import type { CommunicationThread, CommunicationMessage, ThreadsResponse, MessagesResponse } from '../types/api';

// ============================================================
// Type Conversions: Event Payload -> RTK Cache Types
// ============================================================

/**
 * Convert ThreadPayload.thread to CommunicationThread cache format
 */
export function threadPayloadToCacheThread(
  payload: ThreadPayload
): CommunicationThread {
  const { thread } = payload;
  return {
    id: thread.id,
    workspaceId: thread.workspaceId,
    primaryMedium: thread.primaryMedium as CommunicationThread['primaryMedium'],
    source: (thread.source || 'internal') as CommunicationThread['source'],
    externalAddress: thread.externalAddress,
    subject: thread.subject,
    lastMessageAt: thread.lastMessageAt,
    lastMessagePreview: thread.lastMessagePreview,
    lastMessageMedium: thread.lastMessageMedium as CommunicationThread['lastMessageMedium'],
    unreadCount: thread.unreadCount,
    isArchived: thread.isArchived,
    primaryContactId: thread.primaryContactId,
    createdByUserId: thread.createdByUserId ?? '',
    createdAt: thread.createdAt,
    updatedAt: thread.updatedAt,
    // Enriched contact info available via primaryContact relation
    primaryContact: thread.contact ? {
      id: thread.primaryContactId ?? '',
      fullName: thread.contact.fullName ?? '',
      email: thread.contact.email ?? null,
      phone: thread.contact.phone ?? null,
    } : null,
    // Missing fields with defaults
    channelSummary: null,
    externalId: null,
    isLive: false,
    threadType: (thread as any).threadType || 'channel',
  };
}

/**
 * Convert MessagePayload.message to CommunicationMessage cache format
 */
export function messagePayloadToCacheMessage(
  payload: MessagePayload
): CommunicationMessage {
  const { message } = payload;
  return {
    id: message.id,
    threadId: message.threadId,
    workspaceId: message.workspaceId,
    medium: message.medium as CommunicationMessage['medium'],
    direction: message.direction as CommunicationMessage['direction'],
    status: message.status as CommunicationMessage['status'],
    fromAddress: message.fromAddress,
    toAddress: message.toAddress,
    ccAddresses: null,
    bccAddresses: null,
    subject: message.subject,
    bodyText: message.bodyText,
    bodyHtml: message.bodyHtml,
    attachments: (message.attachments || []) as CommunicationMessage['attachments'],
    externalMessageId: message.externalMessageId,
    inReplyToMessageId: null,
    errorMessage: message.errorMessage,
    sentAt: message.sentAt,
    deliveredAt: message.deliveredAt,
    readAt: message.readAt,
    isFavorite: false,
    contactId: message.contactId,
    senderUserId: message.senderUserId,
    rawMetadata: null,
    createdAt: message.createdAt,
    updatedAt: message.updatedAt,
    // Enriched sender info is available via the sender relation
    sender: message.sender ? { id: message.senderUserId ?? '', fullName: message.sender.fullName ?? null, email: message.sender.email ?? '' } : null,
  };
}

// ============================================================
// Thread List Cache Patch Helpers
// ============================================================

export interface ApplyThreadCreatedOptions {
  /** If true, the thread is inserted at position 0 (top). Default: true */
  insertAtTop?: boolean;
}

/**
 * Apply THREAD_CREATED event to thread list cache.
 * Inserts new thread at the top of the list.
 *
 * @returns Updated ThreadsResponse, or null if no change needed
 */
export function applyThreadCreated(
  cache: ThreadsResponse,
  payload: ThreadPayload,
  options: ApplyThreadCreatedOptions = {}
): ThreadsResponse {
  const { insertAtTop = true } = options;
  const newThread = threadPayloadToCacheThread(payload);

  // Check if thread already exists (avoid duplicates)
  const existingIndex = cache.threads.findIndex((t) => t.id === newThread.id);
  if (existingIndex !== -1) {
    // Thread exists - update it in place
    const updatedThreads = [...cache.threads];
    updatedThreads[existingIndex] = newThread;
    return {
      ...cache,
      threads: updatedThreads,
    };
  }

  // Insert new thread
  const threads = insertAtTop
    ? [newThread, ...cache.threads]
    : [...cache.threads, newThread];

  return {
    ...cache,
    threads,
    pagination: {
      ...cache.pagination,
      total: cache.pagination.total + 1,
    },
  };
}

export interface ApplyMessageCreatedToThreadListOptions {
  /** Thread ID of the currently active/viewed thread (won't increment unread) */
  activeThreadId?: string | null;
  /** Current user's ID (won't increment unread for own messages) */
  currentUserId?: string | null;
}

/**
 * Apply MESSAGE_CREATED event to thread list cache.
 * Updates thread's lastMessagePreview, lastMessageAt, and unreadCount.
 * Moves thread to top of list if not already there.
 *
 * @returns Updated ThreadsResponse, or null if thread not in cache
 */
export function applyMessageCreatedToThreadList(
  cache: ThreadsResponse,
  payload: MessagePayload,
  options: ApplyMessageCreatedToThreadListOptions = {}
): ThreadsResponse | null {
  const { activeThreadId, currentUserId } = options;
  const threadIndex = cache.threads.findIndex((t) => t.id === payload.threadId);

  if (threadIndex === -1) {
    // Thread not in cache - can't patch
    return null;
  }

  const thread = cache.threads[threadIndex];
  const isActiveThread = activeThreadId === thread.id;
  const isOwnMessage = currentUserId && payload.message.senderUserId === currentUserId;

  // Calculate new unread count
  // Don't increment if:
  // - It's the active thread (user is viewing it)
  // - It's the user's own message
  const shouldIncrementUnread = !isActiveThread && !isOwnMessage;
  const newUnreadCount = shouldIncrementUnread ? thread.unreadCount + 1 : thread.unreadCount;

  // Use threadUpdate from payload if available, otherwise construct from message
  const lastMessageAt = payload.threadUpdate?.lastMessageAt || payload.message.sentAt || payload.message.createdAt;
  const lastMessagePreview = payload.threadUpdate?.lastMessagePreview || payload.message.bodyText?.slice(0, 200) || '';
  const lastMessageMedium = payload.threadUpdate?.lastMessageMedium || payload.message.medium;

  // Create updated thread
  const updatedThread: CommunicationThread = {
    ...thread,
    lastMessageAt,
    lastMessagePreview,
    lastMessageMedium: lastMessageMedium as CommunicationThread['lastMessageMedium'],
    unreadCount: newUnreadCount,
    updatedAt: new Date().toISOString(),
  };

  // Move thread to top
  const threads = [
    updatedThread,
    ...cache.threads.slice(0, threadIndex),
    ...cache.threads.slice(threadIndex + 1),
  ];

  return {
    ...cache,
    threads,
  };
}

/**
 * Apply THREAD_READ event to thread list cache.
 * Sets unreadCount to 0 for the specified thread.
 */
export function applyThreadReadToThreadList(
  cache: ThreadsResponse,
  threadId: string,
  unreadCount: number = 0
): ThreadsResponse | null {
  const threadIndex = cache.threads.findIndex((t) => t.id === threadId);
  if (threadIndex === -1) return null;

  const threads = [...cache.threads];
  threads[threadIndex] = {
    ...threads[threadIndex],
    unreadCount,
  };

  return { ...cache, threads };
}

// ============================================================
// Messages List Cache Patch Helpers
// ============================================================

export interface ApplyMessageCreatedOptions {
  /** If true, message is appended to end. If false, prepended. Default: true */
  appendToEnd?: boolean;
}

/**
 * Apply MESSAGE_CREATED event to messages cache.
 * Appends new message to the list.
 *
 * @returns Updated MessagesResponse, or null if no change needed
 */
export function applyMessageCreated(
  cache: MessagesResponse,
  payload: MessagePayload,
  options: ApplyMessageCreatedOptions = {}
): MessagesResponse {
  const { appendToEnd = true } = options;
  const newMessage = messagePayloadToCacheMessage(payload);

  // Check if message already exists (avoid duplicates from optimistic update)
  const existingIndex = cache.messages.findIndex((m) => m.id === newMessage.id);
  if (existingIndex !== -1) {
    // Message exists - update it in place
    const updatedMessages = [...cache.messages];
    updatedMessages[existingIndex] = newMessage;
    return {
      ...cache,
      messages: updatedMessages,
    };
  }

  // Insert new message
  const messages = appendToEnd
    ? [...cache.messages, newMessage]
    : [newMessage, ...cache.messages];

  return {
    ...cache,
    messages,
    pagination: {
      ...cache.pagination,
      total: cache.pagination.total + 1,
    },
  };
}

/**
 * Apply MESSAGE_STATUS_CHANGED event to messages cache.
 * Updates the status of a specific message.
 */
export function applyMessageStatusChanged(
  cache: MessagesResponse,
  messageId: string,
  newStatus: string,
  errorMessage?: string | null,
  deliveredAt?: string | null,
  readAt?: string | null
): MessagesResponse | null {
  const messageIndex = cache.messages.findIndex((m) => m.id === messageId);
  if (messageIndex === -1) return null;

  const messages = [...cache.messages];
  messages[messageIndex] = {
    ...messages[messageIndex],
    status: newStatus as CommunicationMessage['status'],
    ...(errorMessage !== undefined && { errorMessage }),
    ...(deliveredAt !== undefined && { deliveredAt }),
    ...(readAt !== undefined && { readAt }),
  };

  return { ...cache, messages };
}

// ============================================================
// Single Thread Cache Patch Helpers
// ============================================================

/**
 * Apply updates to a single thread in cache (e.g., getThread query)
 */
export function applySingleThreadUpdate(
  thread: CommunicationThread,
  payload: ThreadPayload
): CommunicationThread {
  return threadPayloadToCacheThread(payload);
}

/**
 * Apply message to single thread's metadata
 */
export function applySingleThreadMessageUpdate(
  thread: CommunicationThread,
  payload: MessagePayload,
  options: { isActiveThread?: boolean; currentUserId?: string | null } = {}
): CommunicationThread {
  const { isActiveThread, currentUserId } = options;
  const isOwnMessage = currentUserId && payload.message.senderUserId === currentUserId;
  const shouldIncrementUnread = !isActiveThread && !isOwnMessage;

  const lastMessageMedium = payload.threadUpdate?.lastMessageMedium || payload.message.medium;
  return {
    ...thread,
    lastMessageAt: payload.threadUpdate?.lastMessageAt || payload.message.sentAt || payload.message.createdAt,
    lastMessagePreview: payload.threadUpdate?.lastMessagePreview || payload.message.bodyText?.slice(0, 200) || '',
    lastMessageMedium: lastMessageMedium as CommunicationThread['lastMessageMedium'],
    unreadCount: shouldIncrementUnread ? thread.unreadCount + 1 : thread.unreadCount,
    updatedAt: new Date().toISOString(),
  };
}

// ============================================================
// Message Reactions Cache Patch Helpers
// ============================================================

/**
 * Apply MESSAGE_REACTION_CHANGED event to messages cache.
 * Updates the reactions array on the specified message.
 */
export function applyMessageReactionChanged(
  cache: MessagesResponse,
  payload: MessageReactionPayload
): MessagesResponse | null {
  const messageIndex = cache.messages.findIndex((m) => m.id === payload.messageId);
  if (messageIndex === -1) return null;

  const messages = [...cache.messages];
  messages[messageIndex] = {
    ...messages[messageIndex],
    reactions: payload.reactions,
  };

  return { ...cache, messages };
}

/**
 * Apply MESSAGE_REACTION_CHANGED to a single message in cache
 */
export function applySingleMessageReactionChanged(
  message: CommunicationMessage,
  payload: MessageReactionPayload
): CommunicationMessage {
  return {
    ...message,
    reactions: payload.reactions,
  };
}
